import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import * as Yup from 'yup';

import { Button } from '../../../components/button/Button';
import { InputField } from '../../../components/input/InputField';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { Country, Language, PaymentTerm, SelfBillingMode } from 'src/types/enums';
import { COUNTRY_VALUES } from '../../../utils/address';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { AutocompletePostalcode } from '../../location/components/AutocompletePostalcode';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../order/vatRate/VatRateComboboxField';
import { useMinimalGeneralSettings } from '../../../contexts/minimal-settings-context';
import { nullthrows } from '../../../utils/invariant';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { Card } from 'components/Card';
import { defaultPaymentTerm, SelectPaymentTerm } from 'components/select/SelectPaymentTerm';
import { decimateAndFormatNumber, parseNumberInput } from '../../../utils/number';
import type { CreateSupplierInput } from '../models';
import { isValidIban, isValidOptionalBic } from '@utils/iban';
import { InvoiceChannelsField } from '../../customer/pages/Customer/InvoiceChannelsField';
import { customerFieldsFromInvoiceChannels } from '../../customer/document-delivery';
import { defaultSelfBillChannelsForCountry } from '../self-bill-delivery';
import { SELF_BILLING_MODE_VALUES, selfBillingModeItem } from '../self-billing-mode';

const createSupplierSchema = Yup.object().shape({
  accountingId: Yup.string(),
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  vatNumber: Yup.string().vatNumber().required('Vereist'),
  companyNumber: Yup.string().companyNumber().required('Vereist'),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().nullable().required('Vereist'),
  language: Yup.mixed().nullable().required('Vereist'),
  paymentTerm: Yup.object()
    .shape({
      code: Yup.string().required(),
      value: Yup.string().required(),
      days: Yup.number().required(),
    })
    .required('Vereist'),
  defaultVatRate: Yup.mixed().nullable().required('Vereist'),
  selfBillingMode: Yup.mixed<{ key: SelfBillingMode }>().required('Vereist'),
  iban: Yup.string()
    .test('iban', 'Ongeldige IBAN', (value) => !value?.trim() || isValidIban(value))
    .when('selfBillingMode', {
      is: (mode?: { key: SelfBillingMode }) => mode?.key === SelfBillingMode.SelfBilling,
      then: (schema) => schema.required('IBAN is vereist voor self billing'),
    }),
  bic: Yup.string().test('bic', 'Ongeldige BIC', (value) => isValidOptionalBic(value)),
  selfBillChannels: Yup.array(),
  regularCarrier: Yup.bool().required('Vereist'),
  disableCmrReminders: Yup.bool().required('Vereist'),
  dieselSurchargeEnabled: Yup.bool().required('Vereist'),
  hasCustomDieselSurcharge: Yup.bool().required('Vereist'),
  dieselSurchargePercentage: Yup.string().matches(/^-?[0-9]+(\.[0-9]+)?$/, 'Ongeldig getal'),
});

export interface ICreateSupplierFormProps {
  /** When passed, a cancel button is rendered next to the submit button. */
  onCancel?: () => void;
  /** Must never throw — the caller owns error handling and reporting. */
  onSubmit: (input: CreateSupplierInput) => Promise<void>;
  /** Prefills the name field, e.g. with what the user typed in a combobox. */
  initialName?: string;
}

export const CreateSupplierForm: React.FC<ICreateSupplierFormProps> = (props) => {
  const { onCancel, onSubmit, initialName = '' } = props;
  const { settings } = useMinimalGeneralSettings();

  return (
    <Formik
      initialValues={{
        accountingId: '',
        name: initialName,
        vatNumber: '',
        companyNumber: '',
        street: '',
        streetNumber: '',
        city: '',
        postalCode: '',
        country: COUNTRY_VALUES.find((v) => v.key === Country.Be)!,
        language: LANGUAGE_VALUES.find((v) => v.key === Language.Nl)!,
        paymentTerm: defaultPaymentTerm,
        internalNotes: '',
        truckAmount: '0',
        tautlinerAmount: '0',
        boxTrailerAmount: '0',
        openTrailerAmount: '0',
        defaultVatRate: settings.defaultVatRate ? vatRateToComboboxItem(settings.defaultVatRate) : null,
        selfBillingMode: selfBillingModeItem(SelfBillingMode.RideOverview),
        iban: '',
        bic: '',
        selfBillChannels: defaultSelfBillChannelsForCountry(Country.Be),
        regularCarrier: false,
        disableCmrReminders: false,
        dieselSurchargeEnabled: true,
        hasCustomDieselSurcharge: false,
        dieselSurchargePercentage: decimateAndFormatNumber(settings.dieselSurchargePercentage, 2),
      }}
      validationSchema={createSupplierSchema}
      onSubmit={async (values) => {
        // Destructure rather than `delete values.defaultVatRate` — mutating Formik's live
        // values object without a re-render made a second submit fail validation on a field
        // that still visibly held a value. In a dialog (not remounted on retry) that bites.
        const { defaultVatRate, selfBillChannels, selfBillingMode, ...otherValues } = values;
        const defaultVatRateId = nullthrows(defaultVatRate?.key, 'btw type niet ingevuld');

        await onSubmit({
          ...otherValues,
          ...customerFieldsFromInvoiceChannels(selfBillChannels),
          selfBillingMode: selfBillingMode.key,
          country: values.country.key as Country,
          paymentTerm: values.paymentTerm.code as PaymentTerm,
          language: values.language.key as Language,
          truckAmount: +values.truckAmount,
          tautlinerAmount: +values.tautlinerAmount,
          boxTrailerAmount: +values.boxTrailerAmount,
          openTrailerAmount: +values.openTrailerAmount,
          defaultVatRateId,
          dieselSurchargeEnabled: values.dieselSurchargeEnabled,
          hasCustomDieselSurcharge: values.hasCustomDieselSurcharge,
          dieselSurchargePercentage: values.hasCustomDieselSurcharge
            ? values.dieselSurchargePercentage
              ? parseNumberInput(values.dieselSurchargePercentage, 2)
              : 0
            : 0,
        });
      }}
    >
      {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
        <form
          onSubmit={(evt) => {
            // This form can render inside a dialog that is portaled to <body>, but React
            // propagates `submit` over the fiber tree — without this, the page's own form
            // (e.g. the order form) would submit too.
            evt.stopPropagation();
            handleSubmit(evt);
          }}
          className="grid gap-4"
        >
          <Card title="Bedrijfsgegevens">
            <InputField labelText="Naam" type="text" name="name" />
            <InputField labelText="BTW Nummer" type="text" name="vatNumber" />
            <InputField labelText="Ondernemingsnummer" type="text" name="companyNumber" />
            <div className="flex gap-4">
              <div className="flex-4">
                <InputField labelText="Straat" type="text" name="street" />
              </div>
              <div className="flex-1">
                <InputField labelText="Nr" type="text" name="streetNumber" />
              </div>
            </div>
            <AutocompletePostalcode />
            <SimpleSelectField labelText="Taal" items={LANGUAGE_VALUES} name="language" />
          </Card>

          <Card title="Facturatie">
            <InputField labelText="ID Boekhouding" type="text" name="accountingId" />
            <SelectPaymentTerm
              name="paymentTerm"
              value={values.paymentTerm}
              onChange={(value) => setFieldValue('paymentTerm', value)}
            />
            <VatRateComboboxField labelText="Standaard BTW Tarief" name="defaultVatRate" />
            <SimpleSelectField
              labelText="Ritoverzicht / self billing"
              name="selfBillingMode"
              items={SELF_BILLING_MODE_VALUES}
            />
            <div className="grid gap-4 grid-cols-2">
              <CheckboxField labelText="Vaste vervoerder" name="regularCarrier" />
              <CheckboxField labelText="CMR herinneringen uitschakelen" name="disableCmrReminders" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField
                labelText="IBAN"
                type="text"
                name="iban"
                helperText="Later wijzigen kan enkel via een aanvraag met bewijsstuk."
              />
              <InputField labelText="BIC" type="text" name="bic" />
            </div>
            {/* Kept mounted while hidden, so the channels keep following the country. */}
            <div className={values.selfBillingMode.key === SelfBillingMode.None ? 'hidden' : undefined}>
              <InvoiceChannelsField
                name="selfBillChannels"
                labelText="Verzenden via"
                helperText="Altijd zichtbaar in het carrier portal. Peppol enkel bij self billing."
                country={values.country?.key}
                defaultChannelsForCountry={defaultSelfBillChannelsForCountry}
              />
            </div>
          </Card>

          <Card title="Diesel toeslag">
            <CheckboxField labelText="Gebruikt diesel toeslag" name="dieselSurchargeEnabled" />
            {values['dieselSurchargeEnabled'] && (
              <div>
                <CheckboxField
                  labelText="Eigen diesel toeslag"
                  name="hasCustomDieselSurcharge"
                  isDisabled={!values['dieselSurchargeEnabled']}
                />
                <InputField
                  labelText="Eigen diesel toeslag"
                  type="text"
                  name="dieselSurchargePercentage"
                  step="0.01"
                  isDisabled={!values['dieselSurchargeEnabled'] || !values['hasCustomDieselSurcharge']}
                />
                <div className="text-sm">
                  {`Dieseltoeslag voor transporteur is ${decimateAndFormatNumber(
                    values.dieselSurchargeEnabled
                      ? values.hasCustomDieselSurcharge
                        ? values.dieselSurchargePercentage
                          ? parseNumberInput(values.dieselSurchargePercentage, 2)
                          : 0
                        : settings.dieselSurchargePercentage
                      : 0,
                    2,
                  )}%`}
                </div>
              </div>
            )}
          </Card>

          <Card title="Vloot">
            <div className="grid grid-cols-2 gap-4">
              <InputField labelText="Aantal trekkers" type="number" name="truckAmount" />
              <InputField labelText="Aantal tautliners" type="number" name="tautlinerAmount" />
              <InputField labelText="Aantal box/frigo trailers" type="number" name="boxTrailerAmount" />
              <InputField labelText="Aantal open trailers" type="number" name="openTrailerAmount" />
            </div>
          </Card>

          <Card title="Notities">
            <TextAreaField labelText="Interne notities" name="internalNotes" spellCheck={true} />
          </Card>

          <div className="flex justify-between">
            {onCancel && (
              <Button type="button" color="secondary" isDisabled={isSubmitting} onTrigger={onCancel}>
                Annuleer
              </Button>
            )}

            <Button
              type="submit"
              color="primary"
              iconLeft={<PlusIcon className="button-icon" />}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Maak vervoerder aan
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};
